
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { usePieceStore } from '@/stores/usePieceStore';
import { useSewingStore } from '@/stores/useSewingStore';
import { useInlineQCStore } from '@/stores/useInlineQCStore';
import { useFinalQCStore } from '@/stores/useFinalQCStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { getAvailableAction } from '../domain';
import { StatusBadge } from '../components/UI/StatusBadge';
import { TailorSelector } from '../components/UI/TailorSelector';
import { QCDetailsCard } from '../components/UI/QCDetailsCard';
import { ChevronLeft, User, Clock, AlertTriangle, Calendar, ArrowRight, CheckCircle, XCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

export const PieceDetail: React.FC = () => {
  const { pieceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromScan = location.state?.fromScan === true;

  const { userProfile } = useAuthStore();
  const { samples, tailors, fetchTailors, fetchPieceDetails, defectsConfig, fetchSystemConfig } = usePieceStore();
  const { assignPieceToTailor, completePieceSewing } = useSewingStore();
  const { internalQCPiece } = useInlineQCStore();
  const { qcInspectorPiece } = useFinalQCStore();

  const [pieceDetails, setPieceDetails] = useState<any>(null);

  const [isTailorModalOpen, setTailorModalOpen] = useState(false);
  const [isEndSewingModalOpen, setEndSewingModalOpen] = useState(false);
  const [isQCModalOpen, setQCModalOpen] = useState(false);

  // Defect Selection Selection
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDefects, setSelectedDefects] = useState<Record<string, Record<string, number>>>({});
  const [defectNotes, setDefectNotes] = useState('');

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });

  const [qcResult, setQCResult] = useState<'pass' | 'fail' | null>(null);
  const [processing, setProcessing] = useState(false);

  // Derive piece and sample
  const { piece, sample } = useMemo(() => {
    for (const s of samples) {
      const p = s.sample_pieces?.find(pi => pi.id === pieceId);
      if (p) return { piece: p, sample: s };
    }
    return { piece: null, sample: null };
  }, [samples, pieceId]);

  useEffect(() => {
    fetchSystemConfig();
    fetchTailors();
    if (pieceId) {
      fetchPieceDetails(pieceId).then(setPieceDetails).catch(console.error);
    }
  }, [pieceId]);

  // Build Timeline — cutting dates come from sample_planning (shared across all pieces)
  const prod = sample?.sample_production;
  const plan = sample?.sample_planning;

  // Final QC / Endline QC Attempts helpers - use lazy-loaded details
  const inlineList = (pieceDetails?.inline_qc_attempts as any[]) || [];
  const endlineList = (pieceDetails?.final_qc_attempts as any[]) || [];
  const sewCycles = (pieceDetails?.sewing_cycles as any[]) || [];
  const firstSewingStart = sewCycles[0]?.started_at;


  // Helper to parse defects (Schema: [{category, item, count}])
  const getDefectDetails = (defects: any[]) => {
    if (!defects || !Array.isArray(defects)) return '';
    return defects.map(d => `${d.category}: ${d.count} ${d.item}`).join('; ');
  };

  // Cutting dates from sample_planning (same for all pieces in this sample)
  const cuttingStart = plan?.cutting_start_at;
  const cuttingEnd = plan?.cutting_end_at;

  // Helper for Lead Time (Vanilla JS)
  const getLeadTime = (start: string, end: string) => {
    try {
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      if (isNaN(s) || isNaN(e)) return '--';

      const diffMs = e - s;
      if (diffMs < 0) return '0m';

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0 || days > 0) parts.push(`${hours}h`);
      parts.push(`${mins}m`);

      return parts.join(' ');
    } catch { return '--'; }
  };

  const timeline = useMemo(() => {
    if (!piece) return [];
    const steps: any[] = [
      { id: 'created', label: 'Created', time: piece.created_at, status: 'Completed' as const, details: null, isError: false },
      { id: 'cutting_start', label: 'Cutting Started', time: cuttingStart, status: cuttingStart ? 'On Time' as const : 'Upcoming' as const, details: null, isError: false },
      { id: 'cutting_end', label: 'Cutting Ended', time: cuttingEnd, status: cuttingEnd ? 'On Time' as const : 'Upcoming' as const, details: cuttingStart && cuttingEnd ? `Lead Time: ${getLeadTime(cuttingStart, cuttingEnd)}` : null, isError: false },
    ];

    // Sewing Cycles
    sewCycles.forEach((c, i) => {
      if (c.started_at) steps.push({ id: `sew_in_${i}`, label: i === 0 ? 'Sewing Loaded' : `Resewing Started (${i})`, time: c.started_at, status: 'On Time' as const, details: `Tailor: ${c.tailor_name || 'Unassigned'}`, isError: false });
      if (c.ended_at) steps.push({ id: `sew_out_${i}`, label: i === 0 ? 'Sewing Output' : `Resewing Done (${i})`, time: c.ended_at, status: 'On Time' as const, details: c.started_at ? `Lead Time: ${getLeadTime(c.started_at, c.ended_at)}` : null, isError: false });
    });

    // Inline QC
    inlineList.forEach((a, i) => {
      steps.push({
        id: `inline_${i}`,
        label: a.result === 'Pass' ? 'Inline QC Pass' : `Inline QC Fail (${i + 1})`,
        time: a.at,
        status: a.result === 'Pass' ? 'On Time' as const : 'Delayed' as const,
        details: a.result === 'Pass' ? 'Passed ✅' : getDefectDetails(a.defects),
        isError: a.result === 'Fail'
      });
    });

    // Endline QC
    endlineList.forEach((a, i) => {
      steps.push({
        id: `endline_${i}`,
        label: a.result === 'Pass' ? 'Final QC Pass' : `Final QC Fail (${i + 1})`,
        time: a.at,
        status: a.result === 'Pass' ? 'On Time' as const : 'Delayed' as const,
        details: a.result === 'Pass' ? 'Passed ✅' : getDefectDetails(a.defects),
        isError: a.result === 'Fail'
      });
    });

    return steps.filter(s => s.time).sort((a, b) => new Date(a.time || '').getTime() - new Date(b.time || '').getTime());
  }, [piece, cuttingStart, cuttingEnd, sewCycles, inlineList, endlineList]);

  // Derive QC attempts for the cards below
  const { inlineAttempts, endlineAttempts } = useMemo(() => {
    return {
      inlineAttempts: inlineList.map((a, i) => ({ attempt: i + 1, at: a.at, result: a.result, defects: a.defects })),
      endlineAttempts: endlineList.map((a, i) => ({ attempt: i + 1, at: a.at, result: a.result, defects: a.defects }))
    };
  }, [inlineList, endlineList]);

  // Derive display status
  const inlineStatus = useMemo(() => {
    const last = inlineList[inlineList.length - 1];
    if (!last) return 'Upcoming';
    if (last.result === 'Fail') return 'Rework';
    return 'Pass';
  }, [inlineList]);

  const endlineStatus = useMemo(() => {
    const last = endlineList[endlineList.length - 1];
    if (!last) return 'Upcoming';
    if (last.result === 'Fail') return 'Rework';
    return 'Complete';
  }, [endlineList]);

  // Removed auto-select effect for All Collapsed default

  // Early return MUST be after all hooks
  if (!piece || !sample || !userProfile) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
    </div>
  );

  const availableAction = getAvailableAction(piece.status, userProfile.role);
  const canPerformAction = availableAction && (fromScan || userProfile.role === 'supervisor' || userProfile.role === 'qc' || userProfile.role === 'inline_qc');
  const defectCategories = defectsConfig?.items || { "Other": ["General Defect"] };
  const tailorName = piece.current_tailor_name || 'Unassigned';

  const handleMainAction = () => {
    if (!canPerformAction || processing) return;

    if (availableAction.action === 'assign_tailor') {
      setTailorModalOpen(true);
    } else if (availableAction.action === 'mark_sewing_done') {
      setEndSewingModalOpen(true);
    } else if (availableAction.action === 'internal_qc' || availableAction.action === 'final_qc') {
      setQCModalOpen(true);
    }
  };

  const confirmSewingDone = async () => {
    setProcessing(true);
    const dateIso = new Date(selectedDate).toISOString();
    await completePieceSewing(sample.id, piece.id, dateIso);
    setEndSewingModalOpen(false);
    navigate(-1);
  };

  const changeDefectCount = (item: string, delta: number) => {
    const currentCatMap = selectedDefects[selectedCategory] || {};
    const currentCount = currentCatMap[item] || 0;
    const newCount = currentCount + delta;

    let updatedCatMap = { ...currentCatMap };
    if (newCount <= 0) {
      delete updatedCatMap[item];
    } else {
      updatedCatMap[item] = newCount;
    }

    // optimizing cleanup
    if (Object.keys(updatedCatMap).length === 0) {
      const { [selectedCategory]: _, ...rest } = selectedDefects;
      setSelectedDefects(rest);
    } else {
      setSelectedDefects({ ...selectedDefects, [selectedCategory]: updatedCatMap });
    }
  };

  const countDefects = (cat: string) => {
    const map = selectedDefects[cat] || {};
    return Object.values(map).reduce((a, b) => a + b, 0);
  };
  const totalDefects = Object.values(selectedDefects).reduce((acc, map) => acc + Object.values(map).reduce((a, b: any) => a + b, 0), 0);

  const submitQC = async () => {
    if (!qcResult) return;
    if (qcResult === 'fail' && totalDefects === 0) return; // Validation

    setProcessing(true);
    const dateIso = new Date(selectedDate).toISOString();
    const passed = qcResult === 'pass';
    const notes = ''; // We pass defects object now

    if (availableAction?.action === 'internal_qc') {
      // Pass defects map and notes(optional)
      await internalQCPiece(sample.id, piece.id, passed, passed ? {} : selectedDefects, defectNotes, dateIso);
    } else {
      await qcInspectorPiece(sample.id, piece.id, passed, passed ? {} : selectedDefects, defectNotes, dateIso);
    }

    setQCModalOpen(false);
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-2xl pt-safe px-5 pb-3 border-b border-slate-100/80 sticky top-0 z-40">
        <div className="flex items-center gap-3 py-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 active:bg-slate-200 transition-colors">
            <ChevronLeft size={18} />
          </button>
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Piece #{piece.piece_number}</p>
            <h1 className="text-lg font-bold text-slate-800 leading-none">{sample.styles?.style_code}</h1>
          </div>
        </div>
      </div>

      <div className="px-5 pb-28 animate-page">
        {/* Main Info Card */}
        <div className="mt-4 bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <span className="text-[9px] text-slate-400 uppercase font-bold block mb-1">Size</span>
              <span className="text-lg font-bold text-slate-800">{piece.size}</span>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <span className="text-[9px] text-slate-400 uppercase font-bold block mb-1">Status</span>
              <StatusBadge status={piece.status} />
            </div>
          </div>
        </div>

        {/* Tailor Assignment */}
        {piece.current_tailor_name && (
          <div className="mt-3 flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] animate-card">
            <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
              <User size={16} />
            </div>
            <div>
              <span className="text-[9px] text-slate-400 uppercase font-bold">Assigned To</span>
              <p className="text-sm font-bold text-slate-800">{piece.current_tailor_name}</p>
            </div>
          </div>
        )}



        {/* Activity Timeline */}
        {/* Activity Timeline */}
        <div className="mt-8 px-2">
          <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-6 ml-1">Activity History</h3>

          {timeline.length > 0 ? (
            <div className="relative pl-4 border-l-2 border-slate-100 space-y-8">
              {timeline.map((log, idx) => (
                <div key={idx} className="relative animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                  {/* Dot */}
                  <div className={clsx(
                    "absolute -left-[21px] top-1.5 w-3 h-3 rounded-full border-2 border-white ring-1",
                    log.status === 'Upcoming' ? "bg-slate-200 ring-slate-100" :
                      log.status === 'Delayed' ? "bg-amber-400 ring-amber-100" :
                        log.isError ? "bg-red-500 ring-red-100" : "bg-emerald-500 ring-emerald-100"
                  )} />

                  <div className="flex flex-col gap-1.5">
                    {/* Header: Title + Time */}
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className={clsx("text-sm font-bold", log.status === 'Upcoming' ? "text-slate-400" : "text-slate-800")}>
                          {log.label}
                        </h4>
                        {/* Date/Time Subtitle */}
                        {log.time && (
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-mono mt-0.5">
                            <span className="text-slate-500 font-bold">{new Date(log.time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                            <span className="w-0.5 h-0.5 bg-slate-300 rounded-full" />
                            <span>{new Date(log.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                      </div>

                      {/* Status Badge */}
                      <div className={clsx(
                        "align-top text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
                        log.status === 'Upcoming' ? "bg-slate-50 text-slate-300" :
                          log.status === 'Delayed' ? "bg-amber-50 text-amber-600" :
                            log.isError ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                      )}>
                        {log.status}
                      </div>
                    </div>

                    {/* Details Box */}
                    {log.details && (
                      <div className={clsx(
                        "text-xs p-2.5 rounded-lg border",
                        log.isError ? "bg-red-50 border-red-100 text-red-800" : "bg-slate-50 border-slate-100 text-slate-600"
                      )}>
                        {log.details}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-300 text-xs italic">No activity recorded yet</div>
          )}
        </div>

        {/* QC Details Cards (Moved below Activity) */}
        <div className="mt-8 space-y-4">
          <QCDetailsCard
            title="Inline QC"
            status={inlineStatus as any}
            attempts={inlineAttempts}
            isRework={inlineStatus === 'Rework'}
          />
          <QCDetailsCard
            title="Final QC"
            status={endlineStatus as any}
            attempts={endlineAttempts}
            isRework={endlineStatus === 'Rework'}
          />
        </div>
      </div>

      {/* Action Dock */}
      {availableAction && (
        <div className="fixed bottom-0 left-0 right-0 p-5 bg-white/80 backdrop-blur-xl border-t border-slate-100 z-50 pb-safe animate-slide-up">
          {!canPerformAction && (
            <div className="flex items-center gap-2 mb-3 justify-center text-amber-600 bg-amber-50 p-2 rounded-lg">
              <AlertTriangle size={14} />
              <span className="text-[10px] font-bold uppercase">Scan piece to unlock {availableAction.label}</span>
            </div>
          )}

          {canPerformAction && (
            <button
              onClick={handleMainAction}
              disabled={processing}
              className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-2xl active:scale-95 transition-transform shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
            >
              {availableAction.action === 'internal_qc' || availableAction.action === 'final_qc' ? (
                <>Start Inspection <ArrowRight size={16} /></>
              ) : (
                availableAction.label
              )}
            </button>
          )}
        </div>
      )}

      {/* MODAL: Tailor Assignment */}
      {isTailorModalOpen && (
        <TailorSelector
          onSelect={async (id, name, date) => {
            setProcessing(true);
            try {
              await assignPieceToTailor(sample.id, piece.id, id, name, date);
              setTailorModalOpen(false);
              navigate(-1);
            } catch (error) {
              setProcessing(false);
            }
          }}
          onCancel={() => setTailorModalOpen(false)}
        />
      )}

      {/* MODAL: End Sewing Confirmation */}
      {isEndSewingModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm animate-overlay">
          <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl p-6 animate-modal">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Complete Sewing</h3>
            <div className="mb-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2 flex items-center gap-1">
                <Calendar size={10} /> Completion Date
              </label>
              <input
                type="datetime-local"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 font-mono"
              />
            </div>
            {firstSewingStart && (
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100 text-center">
                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block mb-1">Calculated Lead Time</span>
                <p className="text-xl font-black text-blue-600 leading-none">
                  {getLeadTime(firstSewingStart, new Date(selectedDate).toISOString())}
                </p>
              </div>
            )}
            <button onClick={confirmSewingDone} disabled={processing} className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2">
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : 'Confirm Completion'}
            </button>
            <button onClick={() => setEndSewingModalOpen(false)} className="w-full mt-3 py-3 text-slate-400 font-bold text-xs">Cancel</button>
          </div>
        </div>
      )}

      {/* MODAL: QC Inspection */}
      {isQCModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm animate-overlay">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 animate-modal max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Inspection Result</h3>

            {/* Date Selection */}
            <div className="mb-6 bg-slate-50 p-3 rounded-xl border border-slate-100">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2 flex items-center gap-1">
                <Calendar size={10} /> Inspection Date
              </label>
              <input
                type="datetime-local"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 font-mono"
              />
            </div>

            {/* Pass/Fail Selector */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => setQCResult('pass')}
                className={clsx(
                  "py-3 rounded-xl font-bold flex flex-col items-center gap-1 border-2 transition-all",
                  qcResult === 'pass' ? "bg-emerald-50 border-emerald-500 text-emerald-600" : "bg-white border-slate-100 text-slate-400 opacity-60"
                )}
              >
                <CheckCircle size={20} />
                <span className="text-sm">Pass</span>
              </button>
              <button
                onClick={() => setQCResult('fail')}
                className={clsx(
                  "py-3 rounded-xl font-bold flex flex-col items-center gap-1 border-2 transition-all",
                  qcResult === 'fail' ? "bg-red-50 border-red-500 text-red-600" : "bg-white border-slate-100 text-slate-400 opacity-60"
                )}
              >
                <XCircle size={20} />
                <span className="text-sm">Fail</span>
              </button>
            </div>

            {/* If Fail: Show Defect Form */}
            {qcResult === 'fail' && (
              <div className="animate-fade-in space-y-4 mb-6">
                {/* Vertical Accordion */}
                <div className="space-y-3">
                  {Object.keys(defectCategories || {}).map(cat => {
                    const count = countDefects(cat);
                    const isExpanded = selectedCategory === cat;

                    return (
                      <div key={cat} className={clsx(
                        "border rounded-xl transition-all overflow-hidden",
                        isExpanded ? "border-slate-800 bg-white shadow-md" : "border-slate-200 bg-slate-50"
                      )}>
                        <button
                          onClick={() => setSelectedCategory(isExpanded ? '' : cat)}
                          className="w-full flex items-center justify-between p-4"
                        >
                          <div className="flex items-center gap-2">
                            <span className={clsx("font-bold text-sm", isExpanded ? "text-slate-800" : "text-slate-500")}>
                              {cat}
                            </span>
                            {count > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{count}</span>}
                          </div>
                          <ChevronDown size={16} className={clsx("transition-transform text-slate-400", isExpanded ? "rotate-180" : "")} />
                        </button>

                        {isExpanded && (
                          <div className="p-4 pt-0 animate-slide-down">
                            <div className="grid grid-cols-1 gap-2">
                              {defectCategories[cat]?.map((item: string) => {
                                const itemCount = selectedDefects[cat]?.[item] || 0;
                                const isSelected = itemCount > 0;
                                return (
                                  <div key={item} className={clsx(
                                    "flex items-center justify-between p-3 rounded-lg border transition-all select-none",
                                    isSelected ? "bg-red-50 border-red-100" : "bg-white border-slate-100"
                                  )}>
                                    <span className={clsx("text-sm font-medium", isSelected ? "text-red-700" : "text-slate-600")}>{item}</span>

                                    <div className="flex items-center gap-3">
                                      {itemCount > 0 && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); changeDefectCount(item, -1); }}
                                          className="w-8 h-8 flex items-center justify-center rounded-full border border-red-200 text-red-600 bg-white active:scale-90 transition-transform"
                                        >
                                          -
                                        </button>
                                      )}

                                      <span className={clsx("w-6 text-center font-bold text-sm", itemCount > 0 ? "text-slate-800" : "text-slate-300")}>{itemCount}</span>

                                      <button
                                        onClick={(e) => { e.stopPropagation(); changeDefectCount(item, 1); }}
                                        className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 text-slate-600 bg-white hover:border-blue-400 hover:text-blue-600 active:scale-90 transition-transform"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <textarea value={defectNotes} onChange={e => setDefectNotes(e.target.value)} placeholder="Additional notes..." className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200" rows={3} />
              </div>
            )}

            <button
              onClick={submitQC}
              disabled={!qcResult || (qcResult === 'fail' && totalDefects === 0) || processing}
              className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 active:scale-95 transition-transform disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting Inspection...
                </>
              ) : 'Submit Inspection'}
            </button>
            <button onClick={() => setQCModalOpen(false)} className="w-full mt-3 py-3 text-slate-400 font-bold text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};